import { NegotiationInstruction, UserPreference } from 'api'
import { PreparedStagingOrder } from './selectionTypes'
import { IsAxedDefault, newEmptyNegotiationInstruction, TradeConditionFlagDefault } from 'model'

function areInstructionsCompatible(originalInstruction: NegotiationInstruction, newInstruction: NegotiationInstruction) {
  if (!areDetailsCompatible(originalInstruction, newInstruction)) {
    return false
  }

  if (originalInstruction.counterpartyInstructions.length === 0) {
    return true
  }

  return areCounterpartiesCompatible(originalInstruction, newInstruction)
}

function areCounterpartiesCompatible(originalInstruction: NegotiationInstruction, newInstruction: NegotiationInstruction) {
  let counterpartiesCompatible = true
  if (originalInstruction.counterpartyInstructions.length !== newInstruction.counterpartyInstructions.length) {
    counterpartiesCompatible = false
  } else {
    const newCounterpartyIds = newInstruction.counterpartyInstructions.map(
      counterpartyInstruction => counterpartyInstruction.counterpartyId
    )
    originalInstruction.counterpartyInstructions.forEach(counterpartyInstruction => {
      if (!newCounterpartyIds.includes(counterpartyInstruction.counterpartyId)) {
        counterpartiesCompatible = false
        return
      }
    })
  }
  return counterpartiesCompatible
}

function areDetailsCompatible(originalInstruction: NegotiationInstruction, newInstruction: NegotiationInstruction) {
  return (
    originalInstruction.rfqDurationMins === newInstruction.rfqDurationMins &&
    originalInstruction.csd === newInstruction.csd &&
    originalInstruction.settlementVenue === newInstruction.settlementVenue &&
    originalInstruction.hideCompetition === newInstruction.hideCompetition
  )
}

function createTemplateInstruction(
  currentPreparedStagingOrders: PreparedStagingOrder[],
  newPreparedStagingOrders: PreparedStagingOrder[],
  userPreference: UserPreference
): NegotiationInstruction {
  // if there are any current selections
  if (currentPreparedStagingOrders.length > 0) {
    // return a negotiation instruction with default flags based on the first EXISTING selection - they will already be compatible
    return createInstructionWithDefaultFlags(currentPreparedStagingOrders[0].negotiationInstruction)
  }

  if (newPreparedStagingOrders.length === 0) {
    return newEmptyNegotiationInstruction(userPreference)
  }

  // if there are no current selections but we have new selections then attempt to create a compatible template negotiation instruction
  const instructionsInNewSelections = newPreparedStagingOrders.map(preparedStagingOrder => preparedStagingOrder.negotiationInstruction)

  let allCompatible = true
  for (let index = 1; index < instructionsInNewSelections.length; index++) {
    if (!areInstructionsCompatible(instructionsInNewSelections[index - 1], instructionsInNewSelections[index])) {
      allCompatible = false
      break
    }
  }

  // if all the instructions in the new selections are compatible
  if (allCompatible) {
    // return a negotiation instruction with default flags based on the first NEW selection
    return createInstructionWithDefaultFlags(instructionsInNewSelections[0])
  }

  // otherwise if no current selections, and the new selections are incompatible, return a new empty instruction
  return newEmptyNegotiationInstruction(userPreference)
}

function createInstructionWithDefaultFlags(negotiationInstruction: NegotiationInstruction): NegotiationInstruction {
  return {
    ...negotiationInstruction,
    counterpartyInstructions: negotiationInstruction.counterpartyInstructions.map(counterpartyInstruction => ({
      counterpartyId: counterpartyInstruction.counterpartyId,
      counterpartyAlias: null,
      isAxed: IsAxedDefault,
      axeId: null,
      internalAxeId: null,
      tradeConditionFlag: TradeConditionFlagDefault,
      primeBrokerId: counterpartyInstruction.primeBrokerId,
      repoType: null,
      masterLegalAgreementType: null
    }))
  }
}

export function createPreparedStagingOrders(
  currentPreparedStagingOrders: PreparedStagingOrder[],
  newPreparedStagingOrders: PreparedStagingOrder[],
  userPreference: UserPreference
): PreparedStagingOrder[] {
  const templateInstruction = createTemplateInstruction(currentPreparedStagingOrders, newPreparedStagingOrders, userPreference)
  const compatibleNewPreparedStagingOrders: PreparedStagingOrder[] = newPreparedStagingOrders.map(newPreparedStagingOrder => {
    const negotiationInstruction = newPreparedStagingOrder.negotiationInstruction

    const isCompatible = areInstructionsCompatible(negotiationInstruction, templateInstruction)
    const compatibleNewPreparedStagingOrder: PreparedStagingOrder = {
      ...newPreparedStagingOrder,
      negotiationInstruction: isCompatible
        ? {
            ...negotiationInstruction,
            counterpartyInstructions:
              negotiationInstruction.counterpartyInstructions.length > 0
                ? negotiationInstruction.counterpartyInstructions
                : templateInstruction.counterpartyInstructions
          }
        : templateInstruction,
      clearingDetails: [],
      willOverwrite: !isCompatible
    }

    return compatibleNewPreparedStagingOrder
  })

  return [...currentPreparedStagingOrders, ...compatibleNewPreparedStagingOrders]
}
